/**
 * Helpers for next-gen image formats used with `<Media formats={…}>`.
 * Paths are derived by swapping the raster extension (`.jpg`/`.png` → `.avif`/`.webp`).
 */

export type MediaSource = {
  src: string;
  width: number;
};

export type MediaFormatType = 'image/avif' | 'image/webp';

export type MediaFormat = {
  type: MediaFormatType;
  sources: ReadonlyArray<MediaSource> | string;
};

const RASTER_EXT = /\.(png|jpe?g|gif)$/i;

export function replaceRasterExt(src: string, ext: 'webp' | 'avif'): string {
  if (!RASTER_EXT.test(src)) {
    throw new Error(`Expected a raster path with png/jpg/gif extension, got: ${src}`);
  }
  return src.replace(RASTER_EXT, `.${ext}`);
}

export function mediaSourcesWithExt(
  sources: ReadonlyArray<MediaSource>,
  ext: 'webp' | 'avif',
): MediaSource[] {
  return sources.map(({ src, width }) => ({
    src: replaceRasterExt(src, ext),
    width,
  }));
}

/** AVIF first, then WebP — browsers pick the first supported `<source type>`. */
export function modernMediaFormats(
  sources: ReadonlyArray<MediaSource> | string,
): MediaFormat[] {
  if (typeof sources === 'string') {
    return [
      { type: 'image/avif', sources: replaceRasterExt(sources, 'avif') },
      { type: 'image/webp', sources: replaceRasterExt(sources, 'webp') },
    ];
  }

  return [
    { type: 'image/avif', sources: mediaSourcesWithExt(sources, 'avif') },
    { type: 'image/webp', sources: mediaSourcesWithExt(sources, 'webp') },
  ];
}
